import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight,
  ChevronDown,
  Menu,
  X,
  Users,
  Workflow,
  FlaskConical,
  Bot,
  Plug,
  Smartphone,
  History,
  BookOpen,
} from "lucide-react";
import logo from "../../../assets/logo.svg";
import "../../css/Header.css";
import FrothAiIcon from "../common/FrothAiIcon";
import { getLenis } from "../common/SmoothScroll";

const USER_GUIDE = "https://roboticodigital.gitbook.io/https-frothtestops.com";

const navLinks = [
  { label: "Why TestOps", path: "/why-testops" },
  {
    label: "Solutions",
    items: [
      { label: "By Team", path: "/byteam", icon: Users, desc: "Testers, developers, product and exploratory teams" },
      { label: "By Workflow", path: "/workflow", icon: Workflow, desc: "From requirements to release sign-off" },
    ],
  },
  {
    label: "Platform",
    items: [
      { label: "Test Labs", path: "/test-labs", icon: FlaskConical, desc: "Performance, security and code analysis" },
      { label: "AI Evaluation", path: "/ai-evaluation", icon: Bot, desc: "Score your chatbots and LLM features" },
      { label: "Integrations", path: "/integration", icon: Plug, desc: "Jira, Azure DevOps, GitHub and more" },
    ],
  },
  { label: "FrothAI", path: "/froth-ai", ai: true },
  {
    label: "Resources",
    items: [
      { label: "Change Log", path: "/change-log", icon: History, desc: "What's new in every release" },
      { label: "Mobile App", path: "/mobile-app", icon: Smartphone, desc: "Test management on the go" },
      { label: "User Guide", path: USER_GUIDE, icon: BookOpen, desc: "Step-by-step product documentation", external: true },
    ],
  },
  { label: "Self Service", path: "/login" },
  { label: "Login", path: "https://login.frothtestops.com/", external: true },
];

function NavLink({ item, className, onClick, children }) {
  if (item.external) {
    return (
      <a href={item.path} target="_blank" rel="noreferrer" className={className} onClick={onClick}>
        {children}
      </a>
    );
  }
  return (
    <Link to={item.path} className={className} onClick={onClick}>
      {children}
    </Link>
  );
}

const Header = () => {
  const [dropdown, setDropdown] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(null);
  const location = useLocation();
  const navRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    handleScroll();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close menus after navigating.
  useEffect(() => {
    setDropdown(null);
    setMobileMenuOpen(false);
    setMobileDropdownOpen(null);
  }, [location.pathname]);

  // Close an open dropdown on outside click or Escape.
  useEffect(() => {
    if (!dropdown) return undefined;
    const onDown = e => {
      if (navRef.current && !navRef.current.contains(e.target)) setDropdown(null);
    };
    const onKey = e => e.key === "Escape" && setDropdown(null);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [dropdown]);

  const isActive = path =>
    typeof path === "string" &&
    !path.startsWith("http") &&
    (location.pathname.toLowerCase() === path.toLowerCase() ||
      location.pathname.toLowerCase().startsWith(path.toLowerCase() + "/"));

  const groupActive = link => link.items && link.items.some(i => isActive(i.path));

  // Already on the home page: the route doesn't change, so ScrollToTop won't run —
  // glide back to the top instead.
  const goHome = e => {
    if (location.pathname !== "/" || location.hash) return;
    e.preventDefault();
    setMobileMenuOpen(false);
    const lenis = getLenis();
    if (lenis) lenis.scrollTo(0);
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      {mobileMenuOpen && <div className="mobile-menu-overlay active" onClick={() => setMobileMenuOpen(false)} />}

      {mobileMenuOpen && (
        <div className="mobile-menu-panel active" role="dialog" aria-label="Menu">
          <div className="mobile-menu-header">
            <span className="mobile-menu-title">Menu</span>
            <button className="mobile-menu-close" onClick={() => setMobileMenuOpen(false)} aria-label="Close menu">
              <X size={22} />
            </button>
          </div>
          <nav className="mobile-nav">
            <Link
              to="/"
              className={`mobile-nav-link ${location.pathname === "/" ? "mobile-nav-link-active" : ""}`}
              onClick={goHome}
            >
              Home
            </Link>
            {navLinks.map(link => {
              if (link.items) {
                const open = mobileDropdownOpen === link.label;
                return (
                  <div key={link.label} className="mobile-nav-item">
                    <button
                      className={`mobile-dropdown-toggle ${open ? "active" : ""}`}
                      onClick={() => setMobileDropdownOpen(open ? null : link.label)}
                      aria-expanded={open}
                    >
                      <span>{link.label}</span>
                      <ChevronDown size={16} className={`mobile-dropdown-arrow ${open ? "active" : ""}`} />
                    </button>
                    <div className={`mobile-dropdown-menu ${open ? "active" : ""}`}>
                      {link.items.map(item => (
                        <NavLink
                          key={item.label}
                          item={item}
                          className={`mobile-dropdown-link ${isActive(item.path) ? "mobile-dropdown-link-active" : ""}`}
                        >
                          {item.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                );
              }
              return (
                <NavLink
                  key={link.label}
                  item={link}
                  className={`mobile-nav-link ${isActive(link.path) ? "mobile-nav-link-active" : ""}`}
                >
                  {link.ai && <FrothAiIcon size={16} className="header-ai-icon" />}
                  {link.label}
                </NavLink>
              );
            })}
            <Link to="/contact" className="mobile-nav-link mobile-contact-btn">
              Contact Us
            </Link>
          </nav>
        </div>
      )}

      <header className={`header-main ${isScrolled ? "header-scrolled" : ""}`}>
        <div className="header-container">
          <Link to="/" className="header-logo" aria-label="Froth TestOps home" onClick={goHome}>
            <img src={logo} alt="Froth TestOps" />
          </Link>

          <button
            className={`header-mobile-btn ${mobileMenuOpen ? "active" : ""}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Open menu"
            aria-expanded={mobileMenuOpen}
          >
            <Menu size={24} />
          </button>

          <nav className="header-nav" ref={navRef} aria-label="Main">
            {navLinks.map(link => {
              if (link.items) {
                const open = dropdown === link.label;
                return (
                  <div
                    key={link.label}
                    className="header-nav-item header-dropdown"
                    onMouseEnter={() => setDropdown(link.label)}
                    onMouseLeave={() => setDropdown(null)}
                  >
                    <button
                      type="button"
                      className={`header-nav-link header-nav-button ${groupActive(link) ? "header-nav-link-active" : ""}`}
                      aria-expanded={open}
                      aria-haspopup="true"
                      onClick={() => setDropdown(open ? null : link.label)}
                      onFocus={() => setDropdown(link.label)}
                    >
                      {link.label}
                      <ChevronDown size={14} className={`header-arrow ${open ? "open" : ""}`} aria-hidden="true" />
                    </button>
                    {open && (
                      <div className="header-dropdown-menu">
                        {link.items.map(item => {
                          const Icon = item.icon;
                          return (
                            <NavLink
                              key={item.label}
                              item={item}
                              className={`header-dropdown-link ${isActive(item.path) ? "header-dropdown-link-active" : ""}`}
                            >
                              <span className="header-dropdown-icon">
                                <Icon size={18} aria-hidden="true" />
                              </span>
                              <span>
                                <span className="header-dropdown-label">{item.label}</span>
                                <span className="header-dropdown-desc">{item.desc}</span>
                              </span>
                            </NavLink>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={link.label}
                  item={link}
                  className={`header-nav-link ${isActive(link.path) ? "header-nav-link-active" : ""}`}
                >
                  {link.ai && <FrothAiIcon size={15} className="header-ai-icon" />}
                  {link.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="header-right">
            <Link
              to="/contact"
              className={`header-contact-btn${isActive("/contact") ? " is-current" : ""}`}
              aria-current={isActive("/contact") ? "page" : undefined}
            >
              <span>Contact Us</span>
              <span className="header-contact-btn__arrow" aria-hidden="true">
                <ArrowRight size={14} strokeWidth={2.5} />
              </span>
            </Link>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
